import {
  Box,
  Grid,
  ImageListItem,
  Skeleton,
  Typography,
  useTheme,
} from "@mui/material";
import { ZoomIn } from "@mui/icons-material";

interface ProductImageGalleryProps {
  images?: string[];
  mainImage?: string;
  title?: string;
  selectedImage: number;
  onImageSelect: (index: number) => void;
  onImageClick: () => void;
  isLoading: boolean;
}

export const ProductImageGallery = ({
  images,
  mainImage,
  title,
  selectedImage,
  onImageSelect,
  onImageClick,
  isLoading,
}: ProductImageGalleryProps) => {
  const currentImage = images?.[selectedImage] || mainImage;
  const hasMultipleImages = images && images.length > 1;
  const theme = useTheme();

  return (
    <>
      {/* Main Image */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          background: "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)",
          borderRadius: { xs: 3, sm: 4 },
          p: { xs: 2, sm: 3, md: 4 },
          border: "1px solid #e8e8e8",
          boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: { xs: "3px", sm: "4px" },
            background: `linear-gradient(90deg, ${theme.palette.secondary.main} 0%, ${theme.palette.secondary.dark || theme.palette.secondary.main} 100%)`,
            borderRadius: { xs: "12px 12px 0 0", sm: "16px 16px 0 0" },
          },
        }}
      >
        {isLoading ? (
          <Skeleton
            variant="rectangular"
            sx={{
              borderRadius: { xs: 2, sm: 3 },
              height: { xs: 250, sm: 350, md: 480 },
              width: "100%",
              maxWidth: "100%",
            }}
          />
        ) : (
          <Box
            sx={{
              position: "relative",
              cursor: "pointer",
              borderRadius: { xs: 2, sm: 3 },
              overflow: "hidden",
              "&:hover .zoom-overlay": {
                opacity: 1,
              },
              "&:hover .zoom-icon": {
                opacity: 1,
                transform: "scale(1.1)",
              },
            }}
            onClick={onImageClick}
          >
            <Box
              component="img"
              sx={{
                borderRadius: { xs: 2, sm: 3 },
                maxHeight: { xs: 250, sm: 350, md: 480 },
                maxWidth: "100%",
                objectFit: "contain",
                boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "scale(1.02)",
                  boxShadow: "0 8px 30px rgba(0,0,0,0.15)",
                },
              }}
              src={currentImage}
              alt={title}
              loading="lazy"
            />
            {/* Zoom Overlay */}
            <Box
              className="zoom-overlay"
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "rgba(0, 0, 0, 0.3)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                opacity: 0,
                transition: "opacity 0.3s ease",
                borderRadius: { xs: 2, sm: 3 },
              }}
            >
              <ZoomIn
                className="zoom-icon"
                sx={{
                  color: "white",
                  fontSize: { xs: 32, sm: 40, md: 48 },
                  opacity: 0,
                  transform: "scale(0.8)",
                  transition: "all 0.3s ease",
                  filter: "drop-shadow(0 2px 8px rgba(0,0,0,0.3))",
                }}
              />
            </Box>
          </Box>
        )}
      </Box>

      {/* Thumbnail Gallery */}
      {isLoading ? (
        <Box
          sx={{
            marginTop: { xs: 1.5, sm: 2, md: 3 },
            padding: { xs: 1.5, sm: 2, md: 2.5 },
            background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
            borderRadius: { xs: 2, sm: 3 },
            border: "1px solid #e8e8e8",
            boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
          }}
        >
          <Skeleton
            variant="text"
            sx={{
              fontSize: { xs: 11, sm: 12, md: 13 },
              fontWeight: 600,
              mb: { xs: 1, sm: 1.25, md: 1.5 },
              textAlign: "center",
              width: "30%",
              mx: "auto",
            }}
          />
          <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                sx={{
                  width: { xs: 40, sm: 50, md: 60 },
                  height: { xs: 40, sm: 50, md: 60 },
                  borderRadius: 1,
                }}
              />
            ))}
          </Box>
        </Box>
      ) : (
        hasMultipleImages && (
          <Box
            sx={{
              marginTop: { xs: 1.5, sm: 2, md: 3 },
              padding: { xs: 1.5, sm: 2, md: 2.5 },
              background: "linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%)",
              borderRadius: { xs: 2, sm: 3 },
              border: "1px solid #e8e8e8",
              boxShadow: "0 4px 16px rgba(0,0,0,0.06)",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontSize: { xs: 11, sm: 12, md: 13 },
                fontWeight: 600,
                color: "text.secondary",
                mb: { xs: 1, sm: 1.25, md: 1.5 },
                textAlign: "center",
                textTransform: "uppercase",
                letterSpacing: { xs: 0.3, sm: 0.5 },
              }}
            >
              View Images
            </Typography>
            <Grid
              container
              spacing={1}
              sx={{
                overflowX: "visible",
                paddingX: 1,
                display: "flex",
                justifyContent: "center",
              }}
            >
              {images.map((image, index) => (
                <Grid key={index}>
                  <ImageListItem
                    sx={{
                      cursor: "pointer",
                      border:
                        selectedImage === index
                          ? {
                              xs: `3px solid ${theme.palette.secondary.main}`,
                              md: `4px solid ${theme.palette.secondary.main}`,
                            }
                          : "2px solid #e0e0e0",
                      borderRadius: { xs: 1.5, sm: 2 },
                      width: { xs: 60, sm: 70, md: 80 },
                      height: { xs: 60, sm: 70, md: 80 },
                      overflow: "hidden",
                      flexShrink: 0,
                      marginRight: 1,
                      transition: "all 0.3s ease",
                      boxShadow:
                        selectedImage === index
                          ? `0 4px 12px ${theme.palette.secondary.main}4D`
                          : "0 2px 8px rgba(0,0,0,0.1)",
                      "&:hover": {
                        borderColor:
                          selectedImage === index
                            ? theme.palette.secondary.main
                            : "#bdbdbd",
                        transform: "scale(1.08)",
                        boxShadow: "0 6px 16px rgba(0,0,0,0.15)",
                      },
                    }}
                    onClick={() => onImageSelect(index)}
                  >
                    <img
                      src={image}
                      alt={`Thumbnail ${index}`}
                      width="100%"
                      height="100%"
                      style={{ objectFit: "cover" }}
                    />
                  </ImageListItem>
                </Grid>
              ))}
            </Grid>
          </Box>
        )
      )}
    </>
  );
};
